import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'
import {
    ModalContentContainer,
    CustomSubmissionContentContainer,
    MediaBtnContainer,
    CaptionContainer,
    UrlContainer,
    ToggleCaptionContainer,
    TimeContainer,
} from './styles'
import Loading from 'components/Loading'
import Divider from 'components/Divider'
import MediaViewer, { MediaData } from 'components/MediaViewer'
import { engagementDataFields } from 'components/utils'
import { theme } from 'theme'
import { selectPartiposterProfileById } from 'state/partiposter'
import {
    getEditRequestHistoryList,
    selectEditRequestHistory,
    EditRequestHistoryState,
    clearEditRequestHistoryList,
} from 'state/submissions'
import {
    selectEditRequestHistoryModal,
    closeEditRequestHistoryModal,
    selectSidebarStep,
    setMediaData,
    setMediaModalOpen,
    setSelectedMediaIndex,
    selectSelectedCampaignSummary,
} from 'state/review'
import { selectRequestsAreLoading, State } from 'state'
import { STATE_KEYS } from 'state/requests'
import { StepState, BLOCK_TYPE } from 'state/tasks'
import { formatBackendDate } from 'services/dates'
import { Country } from 'services/api/constants'
import UserProfile from 'components/Tag/UserProfile'
import Text from 'particles/Text'
import Modal from 'particles/Modal'
import RectangleButton from 'particles/Button/RectangleButton'
import Icon from 'components/Icon/Icon'

interface SubmissionHistoryContentProps {
    id: string
    editRequestHistoryHistory: EditRequestHistoryState
    step: StepState
    country: Country
}

interface MediaBtnWrapperProps {
    mediaData: MediaData[]
}

const MediaBtnWrapper: React.FC<MediaBtnWrapperProps> = ({ mediaData }) => {
    const dispatch = useDispatch()
    const setMedia = useCallback((data: MediaData[]) => dispatch(setMediaData(data)), [])
    const setMediaModal = useCallback((isOpen: boolean) => dispatch(setMediaModalOpen(isOpen)), [])
    const setMediaIndex = useCallback((index: number) => dispatch(setSelectedMediaIndex(index)), [])
    return (
        <MediaBtnContainer>
            <MediaViewer
                mediaData={mediaData}
                showMediaIndex={0}
                showPopup={false}
                setModalOpen={setMediaModal}
                setMediaData={setMedia}
                setSelectedMediaIndex={setMediaIndex}
                maxHeight="200px"
            />
        </MediaBtnContainer>
    )
}

interface CustomSubmissionHistoryContentProps {
    id: string
    blockData: any
    editRequestHistoryHistory: EditRequestHistoryState
}

const CustomSubmissionHistoryContent: React.FC<CustomSubmissionHistoryContentProps> = ({
    id,
    blockData,
    editRequestHistoryHistory,
}) => {
    const captionRef = useRef<HTMLDivElement>(null)
    const [isFullCaption, setIsFullCaption] = useState<boolean>(false)
    const [showReadMoreButton, setShowReadMoreButton] = useState<boolean>(false)
    const onCaptionCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setIsFullCaption(e.target.checked)
    }

    const submissionData = editRequestHistoryHistory.data[blockData.uuid]

    useEffect(() => {
        if (
            (blockData.type === BLOCK_TYPE.TEXT_SUBMISSION &&
                submissionData &&
                submissionData.split('\n').length > 1) ||
            (captionRef.current && captionRef.current.offsetHeight < captionRef.current.scrollHeight)
        ) {
            setShowReadMoreButton(true)
        } else {
            setShowReadMoreButton(false)
        }
    }, [captionRef, blockData, submissionData, id])

    if (!blockData) {
        return null
    }

    let SubmissionComponent
    if (blockData.type === BLOCK_TYPE.PHOTO_SUBMISSION) {
        SubmissionComponent = submissionData ? <MediaBtnWrapper mediaData={submissionData} /> : null
    } else if (blockData.type === BLOCK_TYPE.TEXT_SUBMISSION) {
        const firstLineCaption =
            submissionData && submissionData.split('\n').length > 1 ? submissionData.split('\n')[0] : submissionData
        SubmissionComponent = submissionData ? (
            <ToggleCaptionContainer>
                <input type="checkbox" id={'readmore' + id} onChange={onCaptionCheckboxChange} />
                {isFullCaption ? (
                    submissionData.split('\n').map((captionParagraph: string, index: number) => (
                        <div className="inner" key={'caption' + '_' + id + '_' + index}>
                            {captionParagraph}
                        </div>
                    ))
                ) : (
                    <div ref={captionRef} className="inner">
                        {firstLineCaption}
                    </div>
                )}
                {showReadMoreButton && <label htmlFor={'readmore' + id} />}
            </ToggleCaptionContainer>
        ) : null
    } else if (blockData.type === BLOCK_TYPE.URL_SUBMISSION) {
        SubmissionComponent = submissionData ? (
            <UrlContainer>
                <a style={{ wordBreak: 'break-all' }} href={submissionData} target="_blank" rel="noopener noreferrer">
                    {submissionData}
                </a>
            </UrlContainer>
        ) : null
    } else {
        return null
    }

    return (
        <>
            <Text scale="bodyS700">{blockData.title}</Text>
            {SubmissionComponent}
        </>
    )
}

interface PredefinedSubmissionHistoryContentProps {
    id: string
    editRequestHistoryHistory: EditRequestHistoryState
}

const PredefinedSubmissionContent: React.FC<PredefinedSubmissionHistoryContentProps> = ({
    id,
    editRequestHistoryHistory,
}) => {
    const [t] = useTranslation()
    const captionRef = useRef<HTMLDivElement>(null)
    const [isFullCaption, setIsFullCaption] = useState<boolean>(false)
    const [showReadMoreButton, setShowReadMoreButton] = useState<boolean>(false)
    const mediaData =
        editRequestHistoryHistory && editRequestHistoryHistory.data && editRequestHistoryHistory.data.files
    const captionData =
        editRequestHistoryHistory && editRequestHistoryHistory.data && editRequestHistoryHistory.data.caption

    const urlData = editRequestHistoryHistory && editRequestHistoryHistory.data && editRequestHistoryHistory.data.url
    const video_code =
        editRequestHistoryHistory && editRequestHistoryHistory.data && editRequestHistoryHistory.data.video_code

    let engagementData = null

    engagementData =
        editRequestHistoryHistory &&
        editRequestHistoryHistory.data &&
        _.pick(editRequestHistoryHistory.data, engagementDataFields)

    const onCaptionCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        setIsFullCaption(e.target.checked)
    }

    useEffect(() => {
        if (
            (captionData && captionData.split('\n').length > 1) ||
            (captionRef.current && captionRef.current.offsetHeight < captionRef.current.scrollHeight)
        ) {
            setShowReadMoreButton(true)
        } else {
            setShowReadMoreButton(false)
        }
    }, [captionRef, captionData, id])

    const firstLineCaption =
        captionData && captionData.split('\n').length > 1 ? captionData.split('\n')[0] : captionData

    return (
        <>
            {urlData && (
                <>
                    <Text scale="bodyS700">{t('submission.table.urlLabel')}</Text>
                    <UrlContainer>
                        <a style={{ wordBreak: 'break-all' }} href={urlData} target="_blank" rel="noopener noreferrer">
                            {urlData}
                        </a>
                    </UrlContainer>
                </>
            )}
            {video_code && (
                <>
                    <Text scale="bodyS700">{t('submission.table.videoCodeLabel')}</Text>
                    <UrlContainer>
                        <a
                            style={{ wordBreak: 'break-all' }}
                            href={video_code}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {video_code}
                        </a>
                    </UrlContainer>
                </>
            )}
            {mediaData && <MediaBtnWrapper mediaData={mediaData} />}
            {captionData && (
                <ToggleCaptionContainer>
                    <input type="checkbox" id={'readmore' + id} onChange={onCaptionCheckboxChange} />
                    {isFullCaption ? (
                        captionData.split('\n').map((captionParagraph: string, index: number) => (
                            <Text className="inner" key={'caption' + '_' + id + '_' + index} scale="caption400">
                                {captionParagraph}
                            </Text>
                        ))
                    ) : (
                        <Text ref={captionRef} className="inner" scale="caption400">
                            {firstLineCaption}
                        </Text>
                    )}
                    {showReadMoreButton && (
                        <Text htmlFor={'readmore' + id} scale="caption400" color="darkBlue" as="label" />
                    )}
                </ToggleCaptionContainer>
            )}
            {engagementData && (
                <>
                    {engagementData.view_count_screenshot_files && (
                        <MediaBtnWrapper mediaData={engagementData.view_count_screenshot_files} />
                    )}
                    {engagementData.insights_screenshot_files && (
                        <MediaBtnWrapper mediaData={engagementData.insights_screenshot_files} />
                    )}
                    {engagementData.follower_count && (
                        <>
                            <Text scale="bodyS700">{t('submission.table.followerCountLabel')}</Text>
                            <CaptionContainer showFullText={true}>{engagementData.follower_count}</CaptionContainer>
                        </>
                    )}
                    {engagementData.engagement_like_count && (
                        <>
                            <Text scale="bodyS700">{t('engagementMetric.like')}</Text>
                            <CaptionContainer showFullText={true}>
                                {engagementData.engagement_like_count}
                            </CaptionContainer>
                        </>
                    )}
                    {engagementData.engagement_reply_count && (
                        <>
                            <Text scale="bodyS700">{t('engagementMetric.reply')}</Text>
                            <CaptionContainer showFullText={true}>
                                {engagementData.engagement_reply_count}
                            </CaptionContainer>
                        </>
                    )}
                    {engagementData.engagement_retweet_count && (
                        <>
                            <Text scale="bodyS700">{t('engagementMetric.retweet')}</Text>
                            <CaptionContainer showFullText={true}>
                                {engagementData.engagement_retweet_count}
                            </CaptionContainer>
                        </>
                    )}
                    {engagementData.engagement_engagement_count && (
                        <>
                            <Text scale="bodyS700">{t('engagementMetric.engagement')}</Text>
                            <CaptionContainer showFullText={true}>
                                {engagementData.engagement_engagement_count}
                            </CaptionContainer>
                        </>
                    )}
                    {engagementData.engagement_comment_count && (
                        <>
                            <Text scale="bodyS700">{t('engagementMetric.numberComments')}</Text>
                            <CaptionContainer showFullText={true}>
                                {engagementData.engagement_comment_count}
                            </CaptionContainer>
                        </>
                    )}
                    {engagementData.engagement_view_count && (
                        <>
                            <Text scale="bodyS700">{t('engagementMetric.view')}</Text>
                            <CaptionContainer showFullText={true}>
                                {engagementData.engagement_view_count}
                            </CaptionContainer>
                        </>
                    )}
                    {engagementData.engagement_share_count && (
                        <>
                            <Text scale="bodyS700">{t('engagementMetric.share')}</Text>
                            <CaptionContainer showFullText={true}>
                                {engagementData.engagement_share_count}
                            </CaptionContainer>
                        </>
                    )}
                    {engagementData.engagement_tag_count && (
                        <>
                            <Text scale="bodyS700">{t('engagementMetric.tag')}</Text>
                            <CaptionContainer showFullText={true}>
                                {engagementData.engagement_tag_count}
                            </CaptionContainer>
                        </>
                    )}
                    {engagementData.engagement_sticker_tap_count && (
                        <>
                            <Text scale="bodyS700">{t('engagementMetric.stickerTap', 'Number of Sticker Taps')}</Text>
                            <CaptionContainer showFullText={true}>
                                {engagementData.engagement_sticker_tap_count}
                            </CaptionContainer>
                        </>
                    )}
                    {engagementData.engagement_reach_count && (
                        <>
                            <Text scale="bodyS700">{t('engagementMetric.reach')}</Text>
                            <CaptionContainer showFullText={true}>
                                {engagementData.engagement_reach_count}
                            </CaptionContainer>
                        </>
                    )}
                    {engagementData.engagement_impression_count && (
                        <>
                            <Text scale="bodyS700">{t('engagementMetric.impression')}</Text>
                            <CaptionContainer showFullText={true}>
                                {engagementData.engagement_impression_count}
                            </CaptionContainer>
                        </>
                    )}
                    {engagementData.engagement_cta_click_count && (
                        <>
                            <Text scale="bodyS700">
                                {t(
                                    'engagementMetric.ctaButtonClicksBoostedPosts',
                                    'Number of CTA Button Clicks (for boosted posts)',
                                )}
                            </Text>
                            <CaptionContainer showFullText={true}>
                                {engagementData.engagement_cta_click_count}
                            </CaptionContainer>
                        </>
                    )}
                    {engagementData.engagement_save_count && (
                        <>
                            <Text scale="bodyS700">{t('engagementMetric.save')}</Text>
                            <CaptionContainer showFullText={true}>
                                {engagementData.engagement_save_count}
                            </CaptionContainer>
                        </>
                    )}
                </>
            )}
        </>
    )
}

const SubmissionHistoryContent: React.FC<SubmissionHistoryContentProps> = ({ id, editRequestHistoryHistory, step }) => {
    return step.type === 'custom' ? (
        <CustomSubmissionContentContainer>
            {step.data.blocks.map((blockData: any, index: number) => {
                return (
                    <CustomSubmissionHistoryContent
                        key={id + '_' + index}
                        id={id}
                        blockData={blockData}
                        editRequestHistoryHistory={editRequestHistoryHistory}
                    />
                )
            })}
        </CustomSubmissionContentContainer>
    ) : (
        <PredefinedSubmissionContent id={id} editRequestHistoryHistory={editRequestHistoryHistory} />
    )
}

export const HistoryListItem: React.FC<SubmissionHistoryContentProps> = ({
    id,
    editRequestHistoryHistory,
    step,
    country,
}) => {
    const [t] = useTranslation()
    return (
        <>
            <Text scale="bodyM700">{t('submission.review.submissionHeader') + ':'}</Text>
            <SubmissionHistoryContent
                id={id}
                editRequestHistoryHistory={editRequestHistoryHistory}
                step={step}
                country={country}
            />
            {editRequestHistoryHistory.comment && (
                <>
                    <Text scale="bodyS700">{t('submission.review.commentHistoryHeader') + ':'}</Text>
                    {editRequestHistoryHistory.comment.split('\n').map((commentParagraph: string, index: number) => (
                        <CaptionContainer key={'commment' + index} scale="caption400" showFullText>
                            {commentParagraph}
                        </CaptionContainer>
                    ))}
                </>
            )}
            {editRequestHistoryHistory.updated_at && (
                <TimeContainer>{formatBackendDate(editRequestHistoryHistory.updated_at, country)}</TimeContainer>
            )}
            <Divider my={theme.newSpacing.m} />
        </>
    )
}

export const EditRequestHistoryModal: React.FC = () => {
    const [t] = useTranslation()
    const dispatch = useDispatch()
    const { isOpen, submissionProgressUuid, partiposterId } = useSelector(selectEditRequestHistoryModal)
    const selectedCampaignSummary = useSelector(selectSelectedCampaignSummary)
    const isLoading = useSelector(selectRequestsAreLoading([STATE_KEYS.GetEditRequestHistoryList]))
    const profile = useSelector((state: State) =>
        partiposterId ? selectPartiposterProfileById(partiposterId)(state) : undefined,
    )
    const editRequestHistory = useSelector(selectEditRequestHistory)
    const [firstHistory, ...moreHistory] = useSelector(selectEditRequestHistory)
    const sidebarStep = useSelector(selectSidebarStep)

    const [isShowAllHistory, setIsShowAllHistory] = useState<boolean>(false)

    const onCloseModal = useCallback(() => {
        dispatch(closeEditRequestHistoryModal())
        dispatch(clearEditRequestHistoryList())
    }, [])
    useEffect(() => {
        if (submissionProgressUuid) {
            dispatch(
                getEditRequestHistoryList({
                    submissionProgressUuid,
                }),
            )
        }
    }, [submissionProgressUuid])
    if (!isOpen) return null
    return (
        <Modal
            onClose={onCloseModal}
            size="l"
            title={t('modalWindow.editRequestHistoryTitle').toUpperCase()}
            hasIcon={false}
            hasButtonSet={false}
        >
            <ModalContentContainer>
                <UserProfile profileData={profile} className="profile" />
                {isLoading ? (
                    <Loading />
                ) : editRequestHistory && firstHistory && sidebarStep && selectedCampaignSummary ? (
                    <>
                        <HistoryListItem
                            id={'history'}
                            editRequestHistoryHistory={firstHistory}
                            step={sidebarStep}
                            country={selectedCampaignSummary.country}
                        />

                        {editRequestHistory.length > 1 ? (
                            isShowAllHistory ? (
                                <>
                                    {moreHistory.map((history, index) => (
                                        <HistoryListItem
                                            id={'history' + index}
                                            key={'history' + index}
                                            editRequestHistoryHistory={history}
                                            step={sidebarStep}
                                            country={selectedCampaignSummary.country}
                                        />
                                    ))}

                                    <RectangleButton
                                        onClick={() => setIsShowAllHistory(false)}
                                        icon="caretUp"
                                        form="text"
                                        size="s"
                                        label={t('button.seeMore')}
                                        color="neutral"
                                        className="show-btn"
                                    />
                                </>
                            ) : (
                                <RectangleButton
                                    onClick={() => setIsShowAllHistory(true)}
                                    icon="caretDown"
                                    form="text"
                                    size="s"
                                    label={t('button.seeMore')}
                                    color="neutral"
                                    className="show-btn"
                                />
                            )
                        ) : null}
                    </>
                ) : (
                    <div className="empty-container">
                        <Icon.EmptyTagIcon />
                        <Text scale="bodyL500">{t('emptyState.noCommentsTitle')}</Text>
                    </div>
                )}
            </ModalContentContainer>
        </Modal>
    )
}
